import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import {
  ErrorText,
  HiddenAudio,
  LoadingHint,
  PlayButton,
  PlayerRow,
  ProgressBlock,
  ProgressInput,
  TimeRow,
} from './Audio.styled';

interface AudioProps {
  src: string;
}

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Fetches remote audio as a blob and returns a blob URL so that seeking works
 * even when the server does not support HTTP Range requests (e.g. Dasha API).
 */
async function getSeekableAudioUrl(url: string): Promise<string> {
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok) throw new Error(`Failed to load audio: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

function PlayIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height="20"
      width="20"
      viewBox="0 0 640 640"
    >
      <path
        fill="#4c4c4c"
        d="M187.2 100.9C174.8 94.1 159.8 94.4 147.6 101.6C135.4 108.8 128 121.9 128 136L128 504C128 518.1 135.5 531.2 147.6 538.4C159.7 545.6 174.8 545.9 187.2 539.1L523.2 355.1C536 348.1 544 334.6 544 320C544 305.4 536 291.9 523.2 284.9L187.2 100.9z"
      />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      height="20"
      width="20"
      viewBox="0 0 640 640"
    >
      <path
        fill="#4c4c4c"
        d="M176 96C149.5 96 128 117.5 128 144L128 496C128 522.5 149.5 544 176 544L240 544C266.5 544 288 522.5 288 496L288 144C288 117.5 266.5 96 240 96L176 96zM400 96C373.5 96 352 117.5 352 144L352 496C352 522.5 373.5 544 400 544L464 544C490.5 544 512 522.5 512 496L512 144C512 117.5 490.5 96 464 96L400 96z"
      />
    </svg>
  );
}

export const Audio = ({ src }: AudioProps) => {
  const [seekableSrc, setSeekableSrc] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);

  useEffect(() => {
    if (!src) {
      setSeekableSrc(null);
      setError(false);
      return;
    }

    const isRemote = src.startsWith('http://') || src.startsWith('https://');
    if (!isRemote) {
      setSeekableSrc(src);
      setError(false);
      return;
    }

    let cancelled = false;
    setSeekableSrc(null);

    getSeekableAudioUrl(src)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = url;
        setSeekableSrc(url);
        setError(false);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [src]);

  const isRemote = src.startsWith('http://') || src.startsWith('https://');
  const isLoading = isRemote && !seekableSrc;
  const audioSrc = seekableSrc ?? src;
  const ready = !error && !isLoading && !!audioSrc;

  const progressPct =
    duration > 0
      ? Math.min(100, Math.max(0, (currentTime / duration) * 100))
      : 0;

  const syncTimeFromAudio = useCallback(() => {
    const el = audioRef.current;
    if (!el || isSeeking) return;
    setCurrentTime(el.currentTime);
  }, [isSeeking]);

  const onLoadedMetadata = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    setDuration(Number.isFinite(el.duration) ? el.duration : 0);
    setCurrentTime(el.currentTime);
  }, []);

  const onEnded = useCallback(() => {
    setPlaying(false);
    setCurrentTime(audioRef.current?.duration ?? 0);
  }, []);

  const togglePlay = useCallback(async () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
      return;
    }
    try {
      await el.play();
      setPlaying(true);
    } catch {
      setPlaying(false);
    }
  }, [playing]);

  const onSeekInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const el = audioRef.current;
      if (!el || !duration) return;
      const t = (Number(e.target.value) / 1000) * duration;
      el.currentTime = t;
      setCurrentTime(t);
    },
    [duration]
  );

  const seekValue =
    duration > 0 ? Math.round((currentTime / duration) * 1000) : 0;

  useEffect(() => {
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [audioSrc]);

  useEffect(() => {
    if (!isSeeking) return;
    const end = () => setIsSeeking(false);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    return () => {
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
    };
  }, [isSeeking]);

  if (error) {
    return <ErrorText>Unable to load audio.</ErrorText>;
  }

  if (isLoading) {
    return (
      <PlayerRow>
        <PlayButton type="button" disabled aria-label="Loading">
          <PlayIcon />
        </PlayButton>
        <TimeRow title="Loading audio">
          <LoadingHint>0:00 / 0:00</LoadingHint>
        </TimeRow>
        <ProgressBlock>
          <ProgressInput
            $progressPct={0}
            min={0}
            max={1000}
            value={0}
            disabled
            aria-label="Playback progress"
          />
        </ProgressBlock>
      </PlayerRow>
    );
  }

  return (
    <PlayerRow>
      <HiddenAudio
        key={audioSrc}
        ref={audioRef}
        src={audioSrc}
        preload="auto"
        onTimeUpdate={syncTimeFromAudio}
        onLoadedMetadata={onLoadedMetadata}
        onDurationChange={onLoadedMetadata}
        onEnded={onEnded}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      <PlayButton
        type="button"
        disabled={!ready}
        onClick={togglePlay}
        aria-label={playing ? 'Pause' : 'Play'}
      >
        {playing ? <PauseIcon /> : <PlayIcon />}
      </PlayButton>
      <TimeRow title={`${formatTime(currentTime)} / ${formatTime(duration)}`}>
        {formatTime(currentTime)} / {formatTime(duration)}
      </TimeRow>
      <ProgressBlock>
        <ProgressInput
          $progressPct={progressPct}
          min={0}
          max={1000}
          step={1}
          value={seekValue}
          disabled={!ready || !duration}
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={duration || 0}
          aria-valuenow={currentTime}
          onPointerDown={() => setIsSeeking(true)}
          onInput={onSeekInput}
          onChange={onSeekInput}
        />
      </ProgressBlock>
    </PlayerRow>
  );
};
